import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import InputFormField from "components/input-form-field";
import RadioGroupFormField from "components/radio-group-form-field";
import type { OptOutReasonKey } from "features/scheduling-v3/domain/settings-document";

export interface OptOutAnswer<K extends string = OptOutReasonKey> {
  /** `""` while nothing is chosen — the dialog keeps "Switch to classic" disabled then. */
  reason: K | "";
  detail: string;
}

/** An answer the flow may save: a reason, and for "Other", some words (ENG-2210). */
export type CompleteOptOutAnswer<K extends string = OptOutReasonKey> =
  OptOutAnswer<K> & { reason: K };

export const isOptOutAnswerComplete = <K extends string = OptOutReasonKey>(
  answer: OptOutAnswer<K>
): answer is CompleteOptOutAnswer<K> =>
  answer.reason !== "" &&
  (answer.reason !== "other" || answer.detail.trim() !== "");

export interface OptOutReasonOption<K extends string = OptOutReasonKey> {
  value: K;
  label: string;
}

const REASON_OPTIONS: OptOutReasonOption[] = [
  { value: "too_slow", label: "Too slow" },
  { value: "missing_feature", label: "Something I need is missing" },
  { value: "hard_to_read", label: "Hard to read" },
  { value: "couldnt_find", label: "Couldn't find something" },
  { value: "prefer_classic", label: "I just prefer the classic one" },
  { value: "other", label: "Other" }
];

/**
 * ENG-1981 / ENG-2210: the one required question asked when someone goes back to the
 * classic scheduler, as the body of the shared confirm dialog. A reason must be picked, and
 * "Other" needs some words, before "Switch to classic" is enabled.
 *
 * It owns its own answer and reports it through `onChange`, because the shared dialog keeps
 * its `content` in the store and never re-renders it (see `delete-scope-content.component`
 * in scheduling-v3). The caller reads the latest answer when "Switch to classic" is pressed,
 * and enables that button from the same report.
 */
export function OptOutReason<K extends string = OptOutReasonKey>({
  onChange,
  options = REASON_OPTIONS as OptOutReasonOption<K>[],
  question = "What made you switch back?"
}: {
  onChange: (answer: OptOutAnswer<K>) => void;
  /** Another feature's reasons (ENG-2535's side menu); the scheduler's by default. */
  options?: OptOutReasonOption<K>[];
  question?: string;
}) {
  const { control, watch } = useForm<OptOutAnswer<K>>({
    defaultValues: { reason: "", detail: "" } as OptOutAnswer<K>
  });
  const isOther = useWatch({ control, name: "reason" }) === "other";

  useEffect(() => {
    const subscription = watch((values) =>
      onChange({
        reason: (values.reason ?? "") as OptOutAnswer<K>["reason"],
        detail: values.detail ?? ""
      })
    );
    return () => subscription.unsubscribe();
  }, [watch, onChange]);

  return (
    <Stack gap={1.5} sx={{ width: "100%", textAlign: "left" }}>
      <Typography variant="body1">{question}</Typography>
      <RadioGroupFormField<OptOutAnswer<K>>
        name="reason"
        label=""
        options={options}
        control={control}
      />
      <InputFormField<OptOutAnswer<K>>
        name="detail"
        label={isOther ? "Tell us what happened" : "Anything else?"}
        required={isOther}
        control={control}
        multiline
        minRows={2}
        inputProps={{ maxLength: 500 }}
      />
    </Stack>
  );
}
